import { useState } from "react";
import { useNotify, useRefresh, useUpdate } from "ra-core";
import { Car, DoorOpen, MapPin, Pencil, Percent, Phone, Truck, Users, Wrench } from "lucide-react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { AsideSection } from "../misc/AsideSection";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Company } from "../types";

// LDF: Location-Logistik (Anfahrt, Parken, Anlieferung, Aufbau-Regeln) und was dort stattfindet.

export const LOCATION_SECTORS = ["location-hotel", "location-restaurant", "eventlocation", "location-betreiber"];

export const isLocation = (c?: Partial<Company> | null) =>
  !!c && (LOCATION_SECTORS.includes(c.sector ?? "") || c.lead_segment === "Location – Empfehlungspartner");

const FIELDS: { key: keyof Company; label: string; icon: typeof Car; placeholder: string; multiline?: boolean }[] = [
  { key: "district", label: "Bezirk / Lage", icon: MapPin, placeholder: "z. B. Charlottenburg, am Zoo" },
  { key: "capacity", label: "Kapazität", icon: Users, placeholder: "z. B. 250 Dinner / 400 Empfang" },
  { key: "access_info", label: "Anfahrt & Anlieferung", icon: Truck, placeholder: "Zufahrt, Lieferantentor, Uhrzeiten, Anmeldung am Empfang …", multiline: true },
  { key: "parking_info", label: "Parken", icon: Car, placeholder: "Ladezone, Halteverbot beantragen, Parkhaus, Kosten …", multiline: true },
  { key: "loading_info", label: "Wege & Aufzug", icon: DoorOpen, placeholder: "Lastenaufzug, Treppen, Türbreiten, Weg bis zum Saal …", multiline: true },
  { key: "setup_rules", label: "Aufbau-Regeln", icon: Wrench, placeholder: "Aufbau-/Abbauzeiten, offenes Feuer/Kerzen, Wasser, Befestigung, Entsorgung …", multiline: true },
  { key: "venue_contact_info", label: "Vor Ort", icon: Phone, placeholder: "Bankett, Haustechnik, Nummer am Eventtag …", multiline: true },
];

export const LocationInfo = ({ record }: { record: Company }) => {
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<Partial<Company>>({});
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  if (!isLocation(record)) return null;

  const start = () => {
    setValues(Object.fromEntries([...FIELDS.map((f) => [f.key, record[f.key] ?? ""]), ["commission_percent", record.commission_percent ?? ""]]));
    setEditing(true);
  };

  const save = () =>
    update(
      "companies",
      {
        id: record.id,
        data: {
          ...Object.fromEntries(FIELDS.map((f) => [f.key, (values[f.key] as string)?.trim() || null])),
          commission_percent:
            values.commission_percent == null || String(values.commission_percent).trim() === ""
              ? null
              : Number(values.commission_percent),
        },
        previousData: record,
      },
      {
        onSuccess: () => {
          notify("Location-Infos gespeichert", { type: "success" });
          setEditing(false);
          refresh();
        },
      },
    );

  const filled = FIELDS.filter((f) => record[f.key]);

  return (
    <AsideSection title="Location">
      {editing ? (
        <div className="space-y-2">
          {FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="block text-xs text-muted-foreground">{f.label}</span>
              {f.multiline ? (
                <textarea
                  value={(values[f.key] as string) ?? ""}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  className="min-h-16 w-full rounded-md border border-input bg-background p-2 text-sm"
                />
              ) : (
                <input
                  value={(values[f.key] as string) ?? ""}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                />
              )}
            </label>
          ))}
          <label className="block">
            <span className="block text-xs text-muted-foreground">Empfehlungsprovision in % (intern)</span>
            <input
              type="number"
              step="0.5"
              value={(values.commission_percent as number | string) ?? ""}
              onChange={(e) => setValues({ ...values, commission_percent: e.target.value as unknown as number })}
              className="h-8 w-24 rounded-md border border-input bg-background px-2 text-sm"
            />
          </label>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={isPending}>
              Speichern
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Abbrechen
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {filled.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.key} className="flex gap-2 text-sm">
                <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <span className="block text-xs text-muted-foreground">{f.label}</span>
                  <span className="whitespace-pre-line break-words">{record[f.key] as string}</span>
                </div>
              </div>
            );
          })}
          {record.commission_percent != null && (
            <div className="flex gap-2 text-sm">
              <Percent className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <span>Empfehlungsprovision {record.commission_percent} % (intern)</span>
            </div>
          )}
          {!filled.length && (
            <p className="text-xs text-muted-foreground">Noch keine Infos zu Anfahrt, Parken oder Aufbau hinterlegt.</p>
          )}
          <Button size="sm" variant="outline" onClick={start}>
            <Pencil className="h-3.5 w-3.5" /> Location-Infos bearbeiten
          </Button>
        </div>
      )}
    </AsideSection>
  );
};

type LinkedEvent = { id: number; name: string; start_date: string | null; date_text: string | null; status: string };
type LinkedDeal = { id: number; name: string; event_date: string | null; stage: string; amount: number | null };

const fmt = (d: string | null) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("de-DE") : "");

// Events und Aufträge, die an dieser Location stattfinden
export const LocationActivity = ({ companyId }: { companyId: number | string }) => {
  const { data } = useQuery({
    queryKey: ["location-activity", companyId],
    queryFn: async () => {
      const supabase = getSupabaseClient();
      const [events, deals] = await Promise.all([
        supabase.from("market_events").select("id,name,start_date,date_text,status").eq("location_company_id", companyId).order("start_date", { ascending: false }),
        supabase.from("deals").select("id,name,event_date,stage,amount").eq("location_company_id", companyId).order("event_date", { ascending: false }),
      ]);
      return { events: (events.data ?? []) as LinkedEvent[], deals: (deals.data ?? []) as LinkedDeal[] };
    },
  });
  if (!data) return <p className="py-6 text-sm text-muted-foreground">Wird geladen …</p>;
  return (
    <div className="space-y-6 pt-2">
      <section>
        <h3 className="mb-1 text-sm font-medium">Unsere Aufträge hier ({data.deals.length})</h3>
        {data.deals.length ? (
          data.deals.map((d) => (
            <Link key={d.id} to={`/deals/${d.id}/show`} className="flex justify-between border-b border-border py-2 text-sm hover:bg-muted">
              <span>{d.name}</span>
              <span className="text-muted-foreground">{fmt(d.event_date)}</span>
            </Link>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">Noch kein Auftrag an dieser Location.</p>
        )}
      </section>
      <section>
        <h3 className="mb-1 text-sm font-medium">Events an dieser Location ({data.events.length})</h3>
        {data.events.length ? (
          data.events.map((e) => (
            <div key={e.id} className="flex justify-between border-b border-border py-2 text-sm">
              <span>{e.name}</span>
              <span className="text-muted-foreground">{fmt(e.start_date) || e.date_text}</span>
            </div>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">Keine Events aus dem Kalender verknüpft.</p>
        )}
        <Link to="/events" className="mt-2 inline-block text-xs underline">
          Zum Eventkalender
        </Link>
      </section>
    </div>
  );
};

type DealLocationData = Pick<Company, "name" | "address" | "district" | "access_info" | "parking_info" | "loading_info" | "setup_rules" | "venue_contact_info"> & { id: number };

// Kompakte Location-Logistik im Auftrag – das Wichtigste für den Aufbautag
export const DealLocation = ({
  locationId,
  endClientId,
  eventDate,
}: {
  locationId?: number | string | null;
  endClientId?: number | string | null;
  eventDate?: string | null;
}) => {
  const { data } = useQuery({
    queryKey: ["deal-location", locationId, endClientId],
    enabled: !!(locationId || endClientId),
    queryFn: async () => {
      const supabase = getSupabaseClient();
      const [loc, client] = await Promise.all([
        locationId
          ? supabase.from("companies").select("id,name,address,district,access_info,parking_info,loading_info,setup_rules,venue_contact_info").eq("id", locationId).single()
          : Promise.resolve({ data: null }),
        endClientId ? supabase.from("companies").select("id,name").eq("id", endClientId).single() : Promise.resolve({ data: null }),
      ]);
      return { location: loc.data as DealLocationData | null, client: client.data as { id: number; name: string } | null };
    },
  });
  if (!locationId && !endClientId && !eventDate) return null;
  const l = data?.location;
  return (
    <div className="m-4 grid gap-4 sm:grid-cols-3">
      {eventDate && (
        <div>
          <span className="block text-xs text-muted-foreground tracking-wide">Eventdatum</span>
          <span className="text-sm">{fmt(eventDate)}</span>
        </div>
      )}
      {data?.client && (
        <div>
          <span className="block text-xs text-muted-foreground tracking-wide">Endkunde</span>
          <Link to={`/companies/${data.client.id}/show`} className="text-sm underline hover:no-underline">
            {data.client.name}
          </Link>
        </div>
      )}
      {l && (
        <div className="sm:col-span-3 rounded-lg border border-border p-3">
          <span className="block text-xs text-muted-foreground tracking-wide">Location</span>
          <Link to={`/companies/${l.id}/show/location`} className="text-sm font-medium underline hover:no-underline">
            {l.name}
          </Link>
          {(l.address || l.district) && <span className="ml-2 text-xs text-muted-foreground">{[l.address, l.district].filter(Boolean).join(" · ")}</span>}
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {FIELDS.filter((f) => !["district", "capacity"].includes(f.key) && l[f.key as keyof DealLocationData]).map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.key} className="flex gap-2 text-sm">
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div>
                    <span className="block text-xs text-muted-foreground">{f.label}</span>
                    <span className="whitespace-pre-line">{l[f.key as keyof DealLocationData] as string}</span>
                  </div>
                </div>
              );
            })}
          </div>
          {!l.access_info && !l.parking_info && !l.setup_rules && (
            <p className="mt-1 text-xs text-muted-foreground">Noch keine Infos zu Anfahrt, Parken oder Aufbau – bei der Location ergänzen.</p>
          )}
        </div>
      )}
    </div>
  );
};
